import React from "react";
import { useLang } from "../lib/LanguageContext";
import PageHeader from "../components/PageHeader";
import {
  Target, Eye, HeartHandshake, CheckCircle2, Users, GraduationCap,
  BookOpenCheck, BookOpen, School, Building2, Award,
} from "lucide-react";

const HISTORY_IMG = "/gallery/prathmik/img-01.jpg";

const About = () => {
  const { t } = useLang();
  const a = t.aboutPg;

  return (
    <div data-testid="about-page">
      <PageHeader eyebrow={t.nav.about} title={a.title} subtitle={t.vision} testId="about-header" />

      {/* History */}
      <section className="section-pad">
        <div className="container-school grid lg:grid-cols-2 gap-12 items-center">
          <img src={HISTORY_IMG} alt="Legacy" loading="lazy" className="rounded-3xl shadow-xl w-full h-[460px] object-cover" />
          <div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-school-primary">{a.history.title}</h2>
            <p className="mt-5 text-base md:text-lg text-school-muted leading-relaxed">{a.history.p1}</p>
            <p className="mt-4 text-base md:text-lg text-school-muted leading-relaxed">{a.history.p2}</p>
          </div>
        </div>
      </section>

      {/* School Information */}
      <section className="section-pad bg-school-muted/50">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow">{t.nav.about}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-school-primary">{a.schoolInfo.title}</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {a.schoolInfo.items.map((it, i) => (
              <div key={i} className="card-soft p-6" data-testid={`school-info-${i}`}>
                <div className="text-xs font-heading font-semibold uppercase tracking-wider text-school-accent">{it.label}</div>
                <div className="mt-2 font-heading font-bold text-base text-school-text leading-relaxed">{it.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Structure */}
      <section className="section-pad">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow">{t.nav.about}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-school-primary">{a.academic.title}</h2>
            <p className="mt-4 text-base md:text-lg text-school-muted">{a.academic.sub}</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {a.academic.divisions.map((d, i) => {
              const Icons = [School, BookOpen, GraduationCap];
              const Icon = Icons[i] || BookOpenCheck;
              return (
                <div key={i} className="card-soft p-7" data-testid={`academic-div-${i}`}>
                  <div className="w-14 h-14 rounded-2xl bg-school-secondary/20 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-school-primary" />
                  </div>
                  <h3 className="font-heading font-extrabold text-xl text-school-text">{d.title}</h3>
                  <ul className="mt-4 space-y-2">
                    {d.items.map((x, j) => (
                      <li key={j} className="flex items-start gap-2 text-base text-school-muted">
                        <CheckCircle2 className="w-5 h-5 text-school-accent shrink-0 mt-0.5" />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="section-pad bg-school-muted/50">
        <div className="container-school grid md:grid-cols-2 gap-6">
          <div className="card-soft p-8 md:p-10" data-testid="mission-card">
            <div className="w-14 h-14 rounded-2xl bg-school-primary/10 flex items-center justify-center mb-5">
              <Target className="w-7 h-7 text-school-primary" />
            </div>
            <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-school-text">{a.mission.title}</h3>
            <p className="mt-4 text-base md:text-lg text-school-muted leading-relaxed">{a.mission.text}</p>
          </div>
          <div className="card-soft p-8 md:p-10" data-testid="vision-card">
            <div className="w-14 h-14 rounded-2xl bg-school-secondary/20 flex items-center justify-center mb-5">
              <Eye className="w-7 h-7 text-school-primary" />
            </div>
            <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-school-text">{a.visionCard.title}</h3>
            <p className="mt-4 text-base md:text-lg text-school-muted leading-relaxed">{a.visionCard.text}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad">
        <div className="container-school max-w-4xl">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-school-primary text-center">{a.values.title}</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {a.values.items.map((v, i) => (
              <div key={i} className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-school" data-testid={`value-item-${i}`}>
                <CheckCircle2 className="w-6 h-6 text-school-accent shrink-0" />
                <span className="font-heading font-semibold text-lg text-school-text">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* संचालक मंडळ — typography only */}
      <section className="section-pad bg-school-primary text-white">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow !text-school-secondary">{t.nav.about}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-white">{a.leadership.title}</h2>
            <p className="mt-4 text-base md:text-lg text-white/90">{a.leadership.sub}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {a.leadership.members.map((m, i) => (
              <div key={i} className="bg-school-primary p-7 lg:p-8 hover:bg-[var(--school-primary-dark)] transition-colors" data-testid={`leader-card-${i}`}>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-school-secondary mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-heading font-bold text-xl text-white leading-snug">{m.name}</h3>
                <div className="mt-2 h-px w-10 bg-school-secondary" />
                <p className="mt-3 text-sm text-white/90">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* आमचे मुख्याध्यापक */}
      <section className="section-pad">
        <div className="container-school">
          <div className="max-w-3xl text-center mx-auto">
            <HeartHandshake className="w-14 h-14 text-school-accent mx-auto" />
            <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-4xl text-school-primary">{a.principals.title}</h2>
            <p className="mt-4 text-base md:text-lg text-school-muted leading-relaxed">{a.principals.sub}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {a.principals.list.map((pr, i) => (
              <div key={i} className="card-soft p-8 text-center relative overflow-hidden" data-testid={`principal-card-${i}`}>
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-school-secondary/15" />
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-school-primary mx-auto flex items-center justify-center">
                    <GraduationCap className="w-8 h-8 text-school-secondary" />
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-xl text-school-text leading-snug">{pr.name}</h3>
                  <div className="mt-2 inline-block px-3 py-1 rounded-full bg-school-accent/10 text-school-accent text-xs font-heading font-semibold uppercase tracking-wider">
                    {pr.role}
                  </div>
                  <p className="mt-3 text-sm text-school-muted leading-relaxed">{pr.qual}</p>
                  <div className="mt-4 pt-4 border-t border-school">
                    <div className="text-xs uppercase tracking-wider text-school-muted">{a.academic.title.split(" ")[0]}</div>
                    <div className="font-heading font-bold text-base text-school-primary mt-1">{pr.dept}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
